/**
 * The playback command vocabulary and the recovery policy that reads it.
 *
 * A command is data: the engine dispatches one onto a single non-concurrent
 * `Atom.fn`, so issuing any command interrupts whatever is running. Everything
 * that used to be encoded in a session counter — "is this still the current
 * work?" — is fiber interruption now, and everything that decides *what to do
 * about a failure* lives in the pure resolvers below.
 */
import { Data, Match, Predicate, Schema } from 'effect';
import type { Atom } from 'effect/reactivity';
import type { DocPackReadingAnchor } from '../doc-pack/reading-plan';
import type {
  ChunkFetchFailedError,
  ChunkFetchSkippedError,
} from './chunk-fetcher';
import type { AudioDetailFetchError } from '../query/audio-detail';
import type { NotePlaybackTarget } from './note-playback';
import type { CommonLoadParams, PlaybackTarget } from './playback-state';

export const PLAYBACK_LOAD_TIMEOUT_MS = 25_000;

/** Spacing between silent same-chunk retries while inside the fetcher's
 * retry budget. */
export const CHUNK_TRANSIENT_RETRY_DELAY_MS = 3_000;

/** Spacing between a running playback command and the user command that
 * supersedes it, so a scrub burst runs one prelude instead of one per tap.
 * The legacy queue only waited this out while a command was in flight — an
 * idle player flushed immediately — and the port keeps that gate, so a first
 * command still pays no added latency.
 *
 * Whether a command supersedes another is read at the DISPATCH site, not
 * inside the fiber: the registry disposes the superseded fiber (running its
 * finalizer) before the replacement's body starts, so a body always observes
 * an idle player. */
export const PLAYBACK_COMMAND_DEBOUNCE_MS = 200;

/** The load deadline expired before the chunk became playable. Keeps the
 * adapter's `{code, message}` diagnostic shape so logging reads it unchanged. */
export class PlaybackLoadTimeoutError extends Schema.TaggedError<PlaybackLoadTimeoutError>()(
  'PlaybackLoadTimeoutError',
  {
    code: Schema.Literal('playback_load_timeout'),
    message: Schema.String,
    timeoutMs: Schema.Finite,
  },
) {}

export function playbackLoadTimeoutError(timeoutMs: number) {
  return PlaybackLoadTimeoutError.make({
    code: 'playback_load_timeout',
    message: `Audio chunk did not become ready within ${timeoutMs.toString()}ms`,
    timeoutMs,
  });
}

/** A pool adapter reported a playback failure. Built from the adapter's
 * `AudioPoolError` snapshot at the boundary — that snapshot stays the adapter's
 * public contract and is not itself tagged. */
export class AudioPoolPlaybackError extends Schema.TaggedError<AudioPoolPlaybackError>()(
  'AudioPoolPlaybackError',
  {
    code: Schema.String,
    message: Schema.String,
  },
) {}

export const chunkLoadFailureTags = [
  'ChunkFetchSkippedError',
  'ChunkFetchFailedError',
  'PlaybackLoadTimeoutError',
  'AudioPoolPlaybackError',
] as const;

/** Everything that can stop the current chunk from reaching playback. The tag
 * is the only discriminator: it decides the recovery action, the failure class
 * and the `reason` that lands in telemetry. */
export type ChunkLoadFailure =
  | ChunkFetchSkippedError
  | ChunkFetchFailedError
  | PlaybackLoadTimeoutError
  | AudioPoolPlaybackError;

/** The `reason` dashboards read. Derived from the tag rather than passed
 * alongside it, so the two can never disagree. */
export const chunkLoadFailureReason = Match.type<ChunkLoadFailure>().pipe(
  Match.tagsExhaustive({
    PlaybackLoadTimeoutError: () => 'load_timeout' as const,
    AudioPoolPlaybackError: () => 'audio_error' as const,
    ChunkFetchSkippedError: () => 'tts_fetch_error' as const,
    ChunkFetchFailedError: () => 'tts_fetch_error' as const,
  }),
);

/** Policy class for a failure. A fetch attempt carries the classification the
 * fetcher already made; a playback-level failure (load timeout, decoder) is
 * transient evidence against the same chunk. */
function chunkLoadFailureClass(failure: ChunkLoadFailure) {
  return Predicate.isTagged(failure, 'ChunkFetchFailedError')
    ? failure.classification
    : ('transient' as const);
}

/** Fetch failures were already recorded by the fetcher; everything else is new
 * evidence the engine has to report back to it. */
export function countsAsNewChunkEvidence(failure: ChunkLoadFailure) {
  return (
    Predicate.isTagged(failure, 'PlaybackLoadTimeoutError') ||
    Predicate.isTagged(failure, 'AudioPoolPlaybackError')
  );
}

/** What the engine does about a chunk that failed to fetch or play.
 * `advance-past-broken-chunk` is the capped skip from decisions §5: the caller
 * counts it against the incident. */
export type ChunkRecoveryAction =
  | { readonly kind: 'ignore' }
  | { readonly kind: 'fault'; readonly fault: 'network' | 'chunk' }
  | { readonly kind: 'advance-past-broken-chunk' }
  | { readonly kind: 'retry-same-chunk'; readonly delayMs: number };

/** One skip per incident: content genuinely broken. A second means the
 * document is broken, not the chunk — stop instead of eating pages. */
const skipBrokenChunkOnceOrFault = (
  incidentAdvanceCount: number,
): ChunkRecoveryAction =>
  incidentAdvanceCount >= 1
    ? { kind: 'fault', fault: 'chunk' }
    : { kind: 'advance-past-broken-chunk' };

/** Recovery policy for a current-priority fetch the fetcher declined to run.
 * A skip is bookkeeping or scheduling, never evidence against the chunk, so
 * none of these outcomes count against the incident's skip cap. */
export function resolveChunkFetchSkipAction(
  skip: ChunkFetchSkippedError,
): ChunkRecoveryAction {
  switch (skip.reason) {
    case 'superseded': {
      // A newer command owns this load.
      return { kind: 'ignore' };
    }
    case 'cooldown': {
      // Server-directed backoff on the current chunk: hold the loading state
      // and retry when the cooldown lapses. No network is touched.
      return {
        kind: 'retry-same-chunk',
        delayMs: Math.max(CHUNK_TRANSIENT_RETRY_DELAY_MS, skip.retryInMs ?? 0),
      };
    }
    case 'gave-up': {
      return { kind: 'fault', fault: 'chunk' };
    }
    case 'circuit-open': {
      return { kind: 'fault', fault: 'network' };
    }
    // no default
  }
}

/** Recovery policy for a chunk that failed to fetch or play (decisions §5).
 * The failure class is the only input allowed to drive behavior; the raw
 * transport error never is. */
export function resolveChunkPlaybackFailureAction({
  failure,
  retryBudgetExhausted,
  incidentAdvanceCount,
}: {
  readonly failure: Exclude<ChunkLoadFailure, ChunkFetchSkippedError>;
  readonly retryBudgetExhausted: boolean;
  readonly incidentAdvanceCount: number;
}): ChunkRecoveryAction {
  const failureClass = chunkLoadFailureClass(failure);
  // Auth-shaped failure: often a transiently unreadable session cookie (the
  // request went out with no credential) rather than a dead session, so one
  // 401 must not halt playback. Retry while the budget holds — the fetcher's
  // open circuit admits a single probe, so a session that is genuinely broken
  // faults after one more attempt instead of looping.
  if (failureClass === 'session') {
    return retryBudgetExhausted
      ? { kind: 'fault', fault: 'network' }
      : { kind: 'retry-same-chunk', delayMs: CHUNK_TRANSIENT_RETRY_DELAY_MS };
  }
  // This chunk can never load (404/400): content genuinely missing.
  if (failureClass === 'permanent')
    return skipBrokenChunkOnceOrFault(incidentAdvanceCount);
  // Transient, budget intact: silently retry the SAME chunk. Advancing during
  // an outage only digs into colder chunks and silently eats content.
  if (!retryBudgetExhausted)
    return {
      kind: 'retry-same-chunk',
      delayMs: CHUNK_TRANSIENT_RETRY_DELAY_MS,
    };
  // Budget spent. A fetch that never landed is an outage: halt in place. A
  // chunk the network delivered that still cannot play is broken content, and
  // takes the same capped skip.
  return Predicate.isTagged(failure, 'ChunkFetchFailedError')
    ? { kind: 'fault', fault: 'chunk' }
    : skipBrokenChunkOnceOrFault(incidentAdvanceCount);
}

/** The single entry point the load path uses: skips and failures answer to
 * different policies, and the tag says which. */
export function resolveChunkLoadRecovery({
  failure,
  retryBudgetExhausted,
  incidentAdvanceCount,
}: {
  readonly failure: ChunkLoadFailure;
  readonly retryBudgetExhausted: boolean;
  readonly incidentAdvanceCount: number;
}): ChunkRecoveryAction {
  return Predicate.isTagged(failure, 'ChunkFetchSkippedError')
    ? resolveChunkFetchSkipAction(failure)
    : resolveChunkPlaybackFailureAction({
        failure,
        retryBudgetExhausted,
        incidentAdvanceCount,
      });
}

/** No document is loaded, so there is nothing to seek within. */
export class NoLoadedAudioError extends Schema.TaggedError<NoLoadedAudioError>()(
  'NoLoadedAudioError',
  { message: Schema.String },
) {}

/** The document has no playable chunk at the requested position. */
export class NoPlayableChunkError extends Schema.TaggedError<NoPlayableChunkError>()(
  'NoPlayableChunkError',
  { message: Schema.String },
) {}

/** The doc-pack catalog or a page the chunk lookup needed could not be read. */
export class DocPackLoadError extends Schema.TaggedError<DocPackLoadError>()(
  'DocPackLoadError',
  {
    audioId: Schema.String,
    cause: Schema.Unknown,
  },
) {}

/** The store's selection does not match its own `playbackMode` — a doc-pack
 * session without a chunk, a legacy session without ids. Modelled rather than
 * thrown so it lands in the command atom's error channel instead of vanishing
 * into a defect. */
export class PlaybackSelectionInvariantError extends Schema.TaggedError<PlaybackSelectionInvariantError>()(
  'PlaybackSelectionInvariantError',
  { message: Schema.String },
) {}

/** One playback command, dispatched onto the provider's command atom.
 *
 * A tagged union over ONE non-concurrent atom rather than an atom per command:
 * issuing any command must interrupt whatever is running, which is what the
 * single monotonic session counter and the single shared command queue both
 * encoded. Per-command atoms would let a skip and a load run concurrently.
 *
 * Every command RETARGETS playback — it decides what should be loaded — which
 * is what makes interrupt-on-dispatch universally correct. Transport intent
 * (play/pause) is deliberately not a command: it must never interrupt a load
 * it does not replace, so it lives as imperative engine actions. */
export type PlaybackCommand = Data.TaggedEnum<{
  loadAudio: {
    readonly audioId: string;
    readonly chapterId?: string | undefined;
    readonly ttsId?: string | undefined;
    readonly loadParams?: CommonLoadParams | undefined;
    /** Open the document at a note instead of at the saved progress this
     * command otherwise carries. Applied once the detail resolves, and only
     * when the note belongs to the document that resolved — a stale or
     * hand-edited `?note=` can pair document A with a note from document B,
     * whose anchor would be a meaningless position here. */
    readonly noteTarget?: NotePlaybackTarget | undefined;
  };
  skipToChunk: {
    readonly ttsId: string;
    readonly loadParams?: CommonLoadParams | undefined;
  };
  skipToDocPackRange: {
    readonly anchor: DocPackReadingAnchor;
    readonly loadParams?: CommonLoadParams | undefined;
  };
  reloadChunk: {};
  /** Re-enter the recovery policy for a transport failure the load's own fiber
   * can no longer answer: it arrived after the chunk became playable, so that
   * fiber has already settled. `positionMs` is where the chunk was when it
   * dropped, so a silent retry resumes there instead of at the top. */
  recoverPlaybackFailure: {
    readonly error: AudioPoolPlaybackError;
    readonly positionMs: number;
  };
  /** Move to the chunk after the current one. `onExhausted` is what to do at
   * the end of the document: `complete` is a natural finish, `stop` is a
   * recovery skip that ran out of document. */
  advanceToNextChunk: {
    readonly autoPlay: boolean;
    readonly onExhausted: 'complete' | 'stop';
  };
}>;
export const PlaybackCommand = Data.taggedEnum<PlaybackCommand>();

/** A command the user asked for. Latest-wins: a newer one interrupts the
 * older. `supersedes` says whether it landed on a busy lane and therefore pays
 * the coalescing debounce. */
export type PlaybackDispatch = {
  readonly command: PlaybackCommand;
  readonly supersedes: boolean;
};

/** Automated work — today only the chunk-boundary advance — runs in a lane of
 * its own, so it neither interrupts user intent nor is lost to it. `target` is
 * the chunk the work belonged to when it was dispatched: if the user has moved
 * off that chunk by the time the lane frees, the work is stale and is dropped
 * rather than landing on someone else's chunk. */
export type AutomatedPlaybackDispatch = {
  readonly command: PlaybackCommand;
  readonly target: PlaybackTarget | undefined;
};

/** Errors a command can settle with. Recovery-managed chunk failures never
 * reach here — the load path resolves those into player state. */
export type PlaybackCommandError =
  | AudioDetailFetchError
  | DocPackLoadError
  | NoLoadedAudioError
  | NoPlayableChunkError
  | PlaybackSelectionInvariantError;

export type PlaybackCommandAtom = Atom.AtomResultFn<
  PlaybackDispatch,
  void,
  PlaybackCommandError
>;

export type AutomatedPlaybackCommandAtom = Atom.AtomResultFn<
  AutomatedPlaybackDispatch,
  void,
  PlaybackCommandError
>;
